import type { ElementType, HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'
import { useReveal } from '../hooks/useReveal'

/* ---------------------------------------------------------------- Container */

export function Container({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('mx-auto w-full max-w-content px-6 sm:px-8', className)}
      {...rest}
    >
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ Section */

type SectionProps = HTMLAttributes<HTMLElement> & {
  /** `false` schaltet das Einblenden beim Scrollen ab (z. B. für den Hero). */
  reveal?: boolean
}

export function Section({
  className,
  children,
  reveal = true,
  ...rest
}: SectionProps) {
  const { ref, shown } = useReveal<HTMLElement>()

  return (
    <section
      ref={reveal ? ref : undefined}
      className={cn(
        'py-20 sm:py-28',
        reveal && 'vcn-reveal',
        reveal && shown && 'vcn-reveal-in',
        className,
      )}
      {...rest}
    >
      {children}
    </section>
  )
}

/* ------------------------------------------------------------------ Eyebrow */

export function Eyebrow({
  className,
  children,
  as: As = 'p',
}: {
  className?: string
  children: ReactNode
  as?: ElementType
}) {
  return <As className={cn('vcn-eyebrow', className)}>{children}</As>
}

/* ------------------------------------------------------------- SectionTitle */

export function SectionTitle({
  as: As = 'h2',
  gradient = false,
  className,
  children,
}: {
  as?: ElementType
  /** Verlaufs-Headline wie im Hero (Mint → Hellblau / Mint → Weiß). */
  gradient?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <As
      className={cn(
        'text-balance font-sans text-3xl leading-[1.15] font-bold sm:text-4xl lg:text-[2.75rem]',
        gradient ? 'vcn-headline-gradient' : 'text-white',
        className,
      )}
    >
      {children}
    </As>
  )
}

/* --------------------------------------------------------------------- Lead */

export function Lead({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <p className={cn('text-pretty text-[0.975rem] leading-relaxed text-white/90 sm:text-base', className)}>
      {children}
    </p>
  )
}

/* --------------------------------------------------------------------- Card */

export function Card({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('vcn-card p-6 sm:p-7', className)} {...rest}>
      {children}
    </div>
  )
}
